import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import CreatePost from './CreatePost';
import './MeinMarktplatz.css';
import './CreatePostPage.css'; // Issue #112: Import styles for camera button
import { AppHeader, BottomNavigation, CreateOptionsMenu, MarketplaceObjectMenu, ConfirmationModal, EditMarketplaceObjectPopover, ImageModal, FloatingPlusButton, ShareModal, MarketplaceHeartButton } from './shared';
import { useLogout, useUserData } from '../hooks';
import { useNavigation } from '../contexts/NavigationContext';
// Issue #101: Removed useGlobalModal - using NavigationContext for create pages
import { useLanguage } from '../contexts/LanguageContext';
import { formatRelativeTime } from '../utils'; // Issue #108: Shared relative time formatting

// Get API base URL from environment variable
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const MeinMarktplatz = ({ initialTab, onTabChange }) => {
  const navigation = useNavigation();
  // Issue #101: Removed openMarketplaceModal - using NavigationContext for create pages
  const { userData } = useUserData();
  const { handleLogout, isLoggingOut, logoutError, clearLogoutError } = useLogout(navigation.onLogout);
  const { t } = useLanguage();

  const [loading, setLoading] = useState(true);
  const [objectsLoading, setObjectsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab || 'favorites'); // Issue #71: Use initialTab from props for page refresh
  const [objects, setObjects] = useState([]);
  const [offset, setOffset] = useState(0);
  const LIMIT = 10; // Issue #190: Load 10 at a time
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = useRef(null);
  const canLoadMore = useRef(false);
  const [showCreateOptions, setShowCreateOptions] = useState(false);
  // Issue #225: State for image gallery modal
  const [selectedImageData, setSelectedImageData] = useState(null);

  // Issue #64: Mark as sold functionality
  const [openMenuObjectId, setOpenMenuObjectId] = useState(null);
  const [confirmSoldObjectId, setConfirmSoldObjectId] = useState(null);
  const [isMarkingSold, setIsMarkingSold] = useState(false);

  // Issue #80: Edit and delete functionality
  const [deletingObjectId, setDeletingObjectId] = useState(null);
  const [isDeletingObject, setIsDeletingObject] = useState(false);
  // Issue #440: field/image/submission state now lives in the shared
  // EditMarketplaceObjectPopover component
  const [editingMarketplaceObject, setEditingMarketplaceObject] = useState(null);

  // Issue #312: Share modal state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareData, setShareData] = useState({ type: '', id: '', content: '', title: '' });

  // Issue #71: Sync activeTab with initialTab prop when it changes (for page refresh)
  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  // Update loading state when userData is loaded from hook
  useEffect(() => {
    if (userData !== null) {
      setLoading(false);
    }
  }, [userData]);

  // Issue #190: Fetch objects with pagination support
  const fetchObjects = useCallback(async (reset = true) => {
    if (!userData) return;

    try {
      if (reset) {
        setObjectsLoading(true);
        canLoadMore.current = false;
      } else {
        setIsLoadingMore(true);
      }

      let response;
      const currentOffset = reset ? 0 : offset;

      if (activeTab === 'favorites') {
        response = await axios.get(`/api/marketplace?favorites_only=true&limit=${LIMIT}&offset=${currentOffset}`);
      } else {
        response = await axios.get(`/api/posts/my-posts?limit=${LIMIT}&offset=${currentOffset}`);
        response.data = response.data.filter(post => post.marketplace_type);
      }

      // Issue #190: Append or replace
      if (reset) {
        setObjects(response.data);
      } else {
        setObjects(prev => [...prev, ...response.data]);
      }

      // Issue #190: Check if there are more to load
      if (response.data.length < LIMIT) {
        setHasMore(false);
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching objects:', err);
      setError('Fehler beim Laden der Objekte');
    } finally {
      setObjectsLoading(false);
      setIsLoadingMore(false);
      if (!canLoadMore.current) {
        setTimeout(() => {
          canLoadMore.current = true;
        }, 500);
      }
    }
  }, [userData, activeTab, offset]);

  // Issue #190: Load more objects callback
  const loadMoreObjects = useCallback(() => {
    if (!isLoadingMore && hasMore && !objectsLoading && canLoadMore.current) {
      setOffset(prev => prev + LIMIT);
    }
  }, [isLoadingMore, hasMore, objectsLoading]);

  // Initial fetch and refetch when tab changes
  useEffect(() => {
    setOffset(0);
    setHasMore(true);
    fetchObjects(true);
  }, [userData, activeTab]);

  // Issue #190: Fetch more when offset changes (not initial)
  useEffect(() => {
    if (offset > 0) {
      fetchObjects(false);
    }
  }, [offset]);

  // Issue #190: IntersectionObserver for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !objectsLoading && !isLoadingMore) {
          loadMoreObjects();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, objectsLoading, isLoadingMore, loadMoreObjects]);

  const handlePostCreated = () => {
    // Post created - user can view it in main feed
    console.log('Post created successfully from Mein Marktplatz');
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setObjects([]); // Clear objects
    // Issue #71: Notify parent component about tab change for page state tracking
    if (onTabChange) {
      onTabChange(tab);
    }
  };

  const handleObjectClick = (object) => {
    // Issue #64: Don't allow clicking on sold objects
    if (object.sold_at) {
      return; // Sold objects are not clickable
    }
    navigation.onNavigateToMarketplaceDetail?.(object);
  };

  // Issue #64: Handle mark as sold
  const handleMarkAsSold = (objectId) => {
    setConfirmSoldObjectId(objectId);
    setOpenMenuObjectId(null);
  };

  // Issue #312: Share object handler
  const handleShareObject = (object) => {
    const title = object.title || '';
    setShareData({
      type: 'object',
      id: object.id,
      content: title,
      title: title
    });
    setShareModalOpen(true);
    setOpenMenuObjectId(null);
  };

  const handleCancelMarkSold = () => {
    setConfirmSoldObjectId(null);
  };

  const handleConfirmMarkSold = async () => {
    setIsMarkingSold(true);
    try {
      await axios.post(`/api/marketplace/${confirmSoldObjectId}/mark-sold`);

      // Update the object in the list
      setObjects(prevObjects =>
        prevObjects.map(obj =>
          obj.id === confirmSoldObjectId
            ? { ...obj, sold_at: new Date().toISOString() }
            : obj
        )
      );

      // Close confirmation modal
      setConfirmSoldObjectId(null);
    } catch (err) {
      console.error('Failed to mark object as sold:', err);
      alert(err.response?.data?.message || 'Fehler beim Markieren als verkauft');
    } finally {
      setIsMarkingSold(false);
    }
  };

  // Issue #80: Edit marketplace object handler
  const handleEditObject = (object) => {
    setEditingMarketplaceObject(object);
    setOpenMenuObjectId(null);
  };

  // Issue #440: Save handler for shared EditMarketplaceObjectPopover
  const handleSaveMarketplaceEdit = (updatedObject) => {
    setObjects(prevObjects =>
      prevObjects.map(obj => obj.id === updatedObject.id ? updatedObject : obj)
    );
    setEditingMarketplaceObject(null);
  };

  // Issue #80: Delete marketplace object handler
  const handleDeleteObject = (objectId) => {
    setDeletingObjectId(objectId);
    setOpenMenuObjectId(null);
  };

  const handleCancelDelete = () => {
    setDeletingObjectId(null);
  };

  const handleConfirmDelete = async () => {
    setIsDeletingObject(true);
    try {
      await axios.delete(`/api/posts/${deletingObjectId}`);
      setObjects(prevObjects => prevObjects.filter(obj => obj.id !== deletingObjectId));
      setDeletingObjectId(null);
    } catch (err) {
      console.error('Failed to delete object:', err);
      alert(err.response?.data?.message || 'Fehler beim Löschen des Objekts');
    } finally {
      setIsDeletingObject(false);
    }
  };

  // Show loading state while checking user data
  if (loading) {
    return (
      <div className="mein-marktplatz-container">
        <p className="loading-text">Lädt...</p>
      </div>
    );
  }

  return (
    <div className="mein-marktplatz-container">
      {/* Header Navigation Bar - Using shared AppHeader component */}
      <AppHeader
        userData={userData}
        onLogout={handleLogout}
        isLoggingOut={isLoggingOut}
        logoutError={logoutError}
        onClearLogoutError={clearLogoutError}
      />

      {/* Tab Navigation */}
      <div className="tabs-container">
        <button
          className={`tab-button ${activeTab === 'favorites' ? 'active' : ''}`}
          onClick={() => handleTabChange('favorites')}
        >
          {t('tabs.favorites')}
        </button>
        <button
          className={`tab-button ${activeTab === 'myObjects' ? 'active' : ''}`}
          onClick={() => handleTabChange('myObjects')}
        >
          {t('tabs.myObjects')}
        </button>
      </div>

      {/* Objects List */}
      <div className="objects-list">
        {objectsLoading ? (
          <p className="loading-text">{t('marketplace.loadingObjects')}</p>
        ) : error ? (
          <p className="error-text">{error}</p>
        ) : objects.length === 0 ? (
          <div className="no-objects">
            <p>
              {activeTab === 'favorites'
                ? t('marketplace.noFavorites')
                : t('marketplace.noOwnObjects')}
            </p>
          </div>
        ) : (
          objects.map((object) => (
            <div
              key={object.id}
              className={`object-card ${object.sold_at ? 'sold' : ''}`}
              onClick={() => handleObjectClick(object)}
              style={{ cursor: object.sold_at ? 'not-allowed' : 'pointer' }}
            >
              {/* Issue #64: Sold badge, Issue #329: Show GEFUNDEN for search objects */}
              {object.sold_at && (
                <div className="sold-badge">
                  {object.marketplace_type === 'search' ? t('marketplace.found') : t('marketplace.sold')}
                </div>
              )}

              {/* Issue #225: Object Image - Clickable to open gallery modal */}
              {object.images && object.images.length > 0 && (
                <div className="object-image-container">
                  <img
                    src={`${API_BASE_URL}${object.images[0]}`}
                    alt={object.title}
                    className="object-image"
                    role="button"
                    tabIndex={0}
                    aria-label={t('marketplace.openImageGallery') || 'Bild in Galerie öffnen'}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImageData({
                        images: [`${API_BASE_URL}${object.images[0]}`],
                        index: 0
                      });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        setSelectedImageData({
                          images: [`${API_BASE_URL}${object.images[0]}`],
                          index: 0
                        });
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                </div>
              )}

              {/* Object Info */}
              <div className="object-info">
                {/* Issue #108: Consistent timestamp display - author name with relative time below */}
                <div className="object-header">
                  <span className="object-author">{object.author_name}</span>
                  <span className="object-time">
                    {formatRelativeTime(object.created_time, t)}
                  </span>
                </div>

                <h3 className="object-title">{object.title}</h3>

                {/* Issue #83: Show "Zum Verschenken" for free giveaways, otherwise show price */}
                {(object.price || object.is_free_giveaway) && (
                  <p className="object-price">
                    {object.is_free_giveaway ? t('marketplace.freeGiveaway') : `${object.price} €`}
                  </p>
                )}

                {/* Issue #329: Show unavailable message for sold objects in Favoriten tab */}
                {activeTab === 'favorites' && object.sold_at && (
                  <p className="object-unavailable-message" role="status" aria-live="polite">
                    {t('marketplace.objectNoLongerAvailable')}
                  </p>
                )}

                {/* Issue #60: Row 3: Hearts and Clicks counts - right-aligned like post actions */}
                <div className="post-footer">
                  <div className="post-actions">
                    {/* Issue #224 / #385: Shared heart button — disabled for sold objects */}
                    <MarketplaceHeartButton
                      className="marketplace-stat-item"
                      heartsCount={object.hearts_count}
                      userHasHearted={object.user_has_hearted}
                      isSold={!!object.sold_at}
                      onToggle={async () => {
                        try {
                          const response = await axios.post(`/api/marketplace/${object.id}/heart`);
                          setObjects(prevObjects => prevObjects.map(obj =>
                            obj.id === object.id
                              ? { ...obj, hearts_count: response.data.hearts_count, user_has_hearted: response.data.user_has_hearted }
                              : obj
                          ));
                        } catch (err) {
                          console.error('Error toggling heart:', err);
                        }
                      }}
                    />

                    {/* Clicks count */}
                    <div className="marketplace-stat-item">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="var(--brand-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="12" cy="12" r="3" stroke="var(--brand-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span style={{
                        fontFamily: 'Poppins, sans-serif',
                        fontWeight: '400',
                        fontSize: '13px',
                        color: 'var(--text-secondary)'
                      }}>
                        {object.clicks_count || 0}
                      </span>
                    </div>

                    {/* Issue #64: 3-dot menu - only in "Meine Objekte" tab and not for sold objects */}
                    {/* Issue #115: Using shared MarketplaceObjectMenu component */}
                    {activeTab === 'myObjects' && !object.sold_at && userData && object.user_id === userData.id && (
                      <div className="post-menu" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="menu-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuObjectId(openMenuObjectId === object.id ? null : object.id);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              setOpenMenuObjectId(null);
                            } else if (e.key === 'ArrowDown' && openMenuObjectId === object.id) {
                              e.preventDefault();
                              const menu = e.currentTarget.parentElement.querySelector('[role="menu"]');
                              const firstItem = menu?.querySelector('[role="menuitem"]');
                              firstItem?.focus();
                            }
                          }}
                          title={t('marketplace.options')}
                          aria-haspopup="menu"
                          aria-expanded={openMenuObjectId === object.id}
                          aria-label={t('marketplace.options')}
                        >
                          ⋮
                        </button>
                        {/* Issue #312: Added onShare for sharing marketplace objects */}
                        {/* Issue #329: Added marketplaceType for correct button text */}
                        <MarketplaceObjectMenu
                          isOpen={openMenuObjectId === object.id}
                          onClose={() => setOpenMenuObjectId(null)}
                          onEdit={() => handleEditObject(object)}
                          onDelete={() => handleDeleteObject(object.id)}
                          onMarkAsSold={() => handleMarkAsSold(object.id)}
                          onShare={() => handleShareObject(object)}
                          isOwner={true}
                          showMarkAsSold={true}
                          marketplaceType={object.marketplace_type}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}

          {/* Issue #190: Load more indicator */}
          {isLoadingMore && (
            <div className="posts-loading-more" role="status" aria-live="polite">{t('loadingMorePosts')}</div>
          )}

          {/* Issue #190: Sentinel element for infinite scroll */}
          {!objectsLoading && hasMore && !isLoadingMore && objects.length > 0 && (
            <div ref={loadMoreRef} className="posts-load-more-sentinel" aria-hidden="true" />
          )}

          {/* Issue #190: End of list message */}
          {!objectsLoading && !hasMore && objects.length > 0 && (
            <div className="posts-end-of-list" role="status">{t('allPostsLoaded')}</div>
          )}
      </div>

      {/* Issue #231: Floating Plus Button for quick marketplace object creation */}
      <FloatingPlusButton
        onClick={() => navigation.onNavigateToCreateMarketplace()}
        ariaLabel={t('floatingPlusButton.createMarketplace') || 'Neues Marktplatz-Objekt erstellen'}
      />

      {/* Bottom Navigation Bar - Using shared component */}
      <BottomNavigation
        activeTab="marketplace"
        onCreateClick={() => setShowCreateOptions(!showCreateOptions)}
      />

      {/* Create Options Menu - Using shared component */}
      {/* Issue #101: Removed onCreateMarketplace prop to use NavigationContext */}
      <CreateOptionsMenu
        isOpen={showCreateOptions}
        onClose={() => setShowCreateOptions(false)}
      />

      {/* Issue #64: Confirmation Modal for Mark as Sold */}
      {/* Issue #115: Using shared ConfirmationModal for accessibility */}
      {/* Issue #329: Use correct text based on marketplace_type */}
      <ConfirmationModal
        isOpen={!!confirmSoldObjectId}
        onClose={handleCancelMarkSold}
        onConfirm={handleConfirmMarkSold}
        title={(() => {
          const obj = objects.find(o => o.id === confirmSoldObjectId);
          return obj?.marketplace_type === 'search' ? t('marketplace.markAsFound') : t('marketplace.markAsSold');
        })()}
        message={(() => {
          const obj = objects.find(o => o.id === confirmSoldObjectId);
          return obj?.marketplace_type === 'search' ? t('marketplace.markAsFoundConfirm') : t('marketplace.markAsSoldConfirm');
        })()}
        confirmText={t('yes')}
        confirmingText={t('marketplace.marking')}
        isConfirming={isMarkingSold}
        variant="default"
      />

      {/* Issue #80: Delete Confirmation Modal */}
      {/* Issue #115: Using shared ConfirmationModal for accessibility */}
      <ConfirmationModal
        isOpen={!!deletingObjectId}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={t('marketplace.deleteObject')}
        message={t('marketplace.deleteObjectConfirm')}
        confirmText={t('delete')}
        confirmingText={t('posts.deleting')}
        isConfirming={isDeletingObject}
        variant="danger"
      />

      {/* Issue #80 / #440: Edit Marketplace Object Modal - shared component */}
      {editingMarketplaceObject && (
        <EditMarketplaceObjectPopover
          object={editingMarketplaceObject}
          onClose={() => setEditingMarketplaceObject(null)}
          onSave={handleSaveMarketplaceEdit}
        />
      )}

      {/* Issue #225: Image Modal for preview images */}
      {selectedImageData && (
        <ImageModal
          images={selectedImageData.images}
          initialIndex={selectedImageData.index}
          onClose={() => {
            setSelectedImageData(null);
            if (window.location.hash === '#image-modal') {
              window.history.replaceState(null, '', window.location.pathname + window.location.search);
            }
          }}
        />
      )}

      {/* Issue #312: Share modal for marketplace objects */}
      <ShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        shareUrl={`${window.location.origin}/share/${shareData.type}/${shareData.id}`}
        shareText={t('share.objectMessage').replace('{title}', shareData.content)}
        shareTitle={shareData.title || 'UNIVERSE'}
        contentType={shareData.type}
      />

      {/* CreatePost Component */}
      {userData && (
        <CreatePost
          userName={userData.first_name}
          onPostCreated={handlePostCreated}
        />
      )}
    </div>
  );
};

export default MeinMarktplatz;
